import React from "react";
import { UseFormRegisterReturn } from "react-hook-form";

/** Primitive types */
type Primitive = string | number | boolean;

interface BaseProps<T> {
  options: T[];
  label: string;
  /** Optional RHF register object */
  registration?: UseFormRegisterReturn;
  /** Optional change handler */
  onChange?: (selected: Primitive | undefined) => void;
  includeOption?: string;
  className?: string;
  error?: string;
  disabled?: boolean;
}

// If T is primitive, no getters; otherwise require both getters
export type SelectProps<T> = BaseProps<T> &
  (T extends Primitive ? {} : { getOptionLabel: (option: T) => string; getOptionValue: (option: T) => string });

export function Select<T>(props: SelectProps<T>): JSX.Element {
  const {
    options,
    error,
    label,
    registration,
    onChange,
    includeOption = "",
    className,
    disabled = false,
    getOptionLabel,
    getOptionValue,
  } = props as SelectProps<T> & Partial<{ getOptionLabel: (o: T) => string; getOptionValue: (o: T) => string }>;

  // React Hook Form wiring
  const { name: rhfName, ref: rhfRef, onBlur: rhfOnBlur, onChange: rhfOnChange } = registration ?? {};

  // Formatters: fallback to String for primitives or missing getters
  const labelFormatter: (opt: T) => string = getOptionLabel ?? ((opt: T) => String(opt));
  const valueFormatter: (opt: T) => string = getOptionValue ?? ((opt: T) => String(opt));

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    // Let RHF track
    if (rhfOnChange) rhfOnChange(e as any);

    if (onChange) {
      const val = e.target.value;
      if (includeOption && val === "") {
        onChange(undefined);
      } else {
        const found = options.find((o) => valueFormatter(o) === val);

        onChange(found ? valueFormatter(found) : undefined);
      }
    }
  };

  const elementId = rhfName ?? `${label.replace(/\s+/g, "-").toLowerCase()}-select`;

  return (
    <div className={className}>
      <label htmlFor={elementId} className="text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <select
        id={elementId}
        name={rhfName}
        ref={rhfRef}
        onBlur={rhfOnBlur}
        onChange={handleChange}
        disabled={disabled || options.length === 0}
        className={`bg-gray-50 border border-gray-300 text-gray-900 text-sm py-3 px-2 rounded-lg focus:ring-blue-500 focus:border-blue-500 focus:outline-none block w-full ${
          disabled ? "bg-gray-100 cursor-not-allowed" : ""
        }`}
      >
        {includeOption !== "" ? <option value="">{includeOption}</option> : null}
        {options.map((opt) => {
          const value = valueFormatter(opt);
          return (
            <option key={value} value={value}>
              {labelFormatter(opt)}
            </option>
          );
        })}
      </select>
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
}
